import { useState, useMemo, useEffect } from "react";
import { useScrollDepth } from "@/lib/scrollTracking";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { allBlogArticles } from "@/data/blogArticles";
import NewsletterSignup from "@/components/blog/NewsletterSignup";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SectionAtmosphere from "@/components/SectionAtmosphere";
import atmosphereBlog from "@/assets/atmosphere-features.jpg";
import { lqipFor } from "@/generated/atmospheric-lqip";
import { letterRise, riseFade, splitChars, easeStandard } from "@/lib/motion";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { prefetchOn, prefetchOnIdle } from "@/lib/prefetchRoute";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import PictureImg from "@/components/ui/PictureImg";

const SITE_URL = "https://myelinahealth.com";
const BLOG_URL = `${SITE_URL}/blog`;
const BLOG_TITLE = "Journal | Living well with MS | Myelina Health";
const BLOG_DESC =
  "Evidence-informed guides on MS fatigue, energy tracking, brain fog, pacing, heat sensitivity, and daily living, written for women living with Multiple Sclerosis.";

function setMetaTag(prop: string, content: string) {
  const isOg = prop.startsWith("og:") || prop.startsWith("article:");
  const attr = isOg ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const Blog = () => {
  useScrollDepth("blog");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = useMemo(
    () => Array.from(new Set(allBlogArticles.map((a) => a.category))),
    [],
  );

  const filtered = useMemo(() => {
    return allBlogArticles
      .filter((a) => {
        const matchesCategory = !activeCategory || a.category === activeCategory;
        const q = search.toLowerCase();
        const matchesSearch =
          !q ||
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.keywords.some((k) => k.includes(q));
        return matchesCategory && matchesSearch;
      })
      // Reverse-chronological. Sorting on the ISO `publishDate` string is safe
      // because YYYY-MM-DD sorts lexically. Guarded so a malformed entry
      // (e.g. "May 17, 2026") sinks to the bottom instead of hopping around.
      .slice()
      .sort((a, b) => (b.publishDate || "").localeCompare(a.publishDate || ""));
  }, [search, activeCategory]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const headline1 = "Living well";
  const headline2 = "with MS.";

  // ── Per-route SEO for /blog ─────────────────────────────
  useEffect(() => {
    // Warm the BlogArticle chunk while the visitor is scanning the index.
    prefetchOnIdle(["/blog/first-article"]);
    const prevTitle = document.title;
    document.title = BLOG_TITLE;
    setMetaTag("description", BLOG_DESC);
    setMetaTag("og:title", BLOG_TITLE);
    setMetaTag("og:description", BLOG_DESC);
    setMetaTag("og:url", BLOG_URL);
    setMetaTag("og:type", "website");
    setMetaTag("og:image", `${SITE_URL}/og-image-large.png`);
    setMetaTag("og:image:secure_url", `${SITE_URL}/og-image-large.png`);
    setMetaTag("og:image:width", "1200");
    setMetaTag("og:image:height", "630");
    setMetaTag("og:image:alt", "Myelina Health Journal, living well with MS");
    setMetaTag("og:site_name", "Myelina Health");
    setMetaTag("og:locale", "en_US");
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", BLOG_TITLE);
    setMetaTag("twitter:description", BLOG_DESC);
    setMetaTag("twitter:image", `${SITE_URL}/og-image-large.png`);
    setMetaTag("twitter:image:alt", "Myelina Health Journal, living well with MS");

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.href;
    canonical.href = BLOG_URL;

    const blogLd = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${BLOG_URL}#blog`,
      name: "Myelina Health Journal",
      description: BLOG_DESC,
      url: BLOG_URL,
      inLanguage: "en",
      publisher: {
        "@type": "Organization",
        name: "Myelina Health",
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.png` },
      },
      blogPost: allBlogArticles.map((a) => ({
        "@type": "BlogPosting",
        headline: a.title,
        url: `${SITE_URL}/blog/${a.slug}`,
        datePublished: a.publishDate,
        image: a.heroImage.startsWith("http") ? a.heroImage : `${SITE_URL}${a.heroImage}`,
        articleSection: a.category,
      })),
    };
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: allBlogArticles.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/blog/${a.slug}`,
        name: a.title,
      })),
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Journal", item: BLOG_URL },
      ],
    };
    const nodes: HTMLScriptElement[] = [];
    for (const [id, data] of [
      ["blog-index-jsonld", blogLd],
      ["blog-index-itemlist-jsonld", itemListLd],
      ["blog-index-breadcrumb-jsonld", breadcrumbLd],
    ] as const) {
      document.getElementById(id)?.remove();
      const s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      nodes.push(s);
    }

    return () => {
      document.title = prevTitle;
      nodes.forEach((n) => n.remove());
      if (canonical) canonical.href = prevCanonical || `${SITE_URL}/`;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <div className="container mx-auto px-6 sm:px-10 lg:px-16 pt-28">
          <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Journal" }]} />
        </div>



      {/* ── Hero ─────────────────────────────────────────── */}
      <SectionAtmosphere
        ariaLabelledBy="blog-heading"
        src={atmosphereBlog}
        optimizedBase="/opt/atmosphere-features"
        lqip={lqipFor(atmosphereBlog)}
        origin="top right"
        intensity={0.12}
        glow="right"
        tone="cream"
        className="pt-40 pb-20 sm:pt-48 sm:pb-24"
      >
        <div className="container mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeStandard }}
              className="eyebrow justify-center mx-auto"
            >
              Knowledge base
            </motion.div>

            <motion.h1
              id="blog-heading"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.022, delayChildren: 0.1 } },
              }}
              className="font-display text-foreground text-display-xl"
              aria-label={`${headline1} ${headline2}`}
            >
              <span className="block">
                {splitChars(headline1).map(({ char, key }) => (
                  <motion.span key={key} variants={letterRise} className="inline-block">{char}</motion.span>
                ))}
              </span>
              <span className="block italic text-primary">
                {splitChars(headline2).map(({ char, key }) => (
                  <motion.span key={key} variants={letterRise} className="inline-block">{char}</motion.span>
                ))}
              </span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="visible"
              variants={riseFade}
              transition={{ delay: 0.4, duration: 0.7, ease: easeStandard }}
              className="lead-italic max-w-2xl mx-auto"
            >
              Field notes on fatigue, energy, and the quiet daily practices that hold a day
              together, for the women navigating MS. Read in your own time.
            </motion.p>

            {/* Search */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={riseFade}
              transition={{ delay: 0.55, duration: 0.6, ease: easeStandard }}
              className="max-w-md mx-auto relative pt-2"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search the library…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-11 h-12 rounded-full bg-background/70 backdrop-blur-sm border-foreground/10"
              />
            </motion.div>

            {/* Categories */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={riseFade}
              transition={{ delay: 0.7, duration: 0.6, ease: easeStandard }}
              className="flex flex-wrap justify-center gap-2 pt-2"
            >
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase font-medium transition-colors min-h-[44px] ${
                  !activeCategory
                    ? "bg-primary text-primary-foreground"
                    : "bg-foreground/[0.04] text-muted-foreground hover:text-foreground"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                  className={`px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase font-medium transition-colors min-h-[44px] ${
                    activeCategory === cat
                      ? "bg-primary text-primary-foreground"
                      : "bg-foreground/[0.04] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </SectionAtmosphere>

      {/* ── Pillar callout ───────────────────────────────── */}
      <section className="bg-background pt-4 pb-2" aria-label="Pillar guides">
        <div className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/blog/pillar/energy-management-ms"
              className="group block rounded-2xl border border-primary/15 bg-primary/[0.04] p-6 sm:p-8 hover:bg-primary/[0.06] hover:border-primary/25 transition-colors"
            >
              <div className="eyebrow mb-2">Pillar guide</div>
              <h2 className="font-display text-2xl sm:text-3xl text-foreground leading-tight">
                MS energy management
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
                Notice your window of energy. Protect it. Spend it on what matters.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-primary group-hover:translate-x-0.5 transition-transform">
                Read the pillar →
              </span>
            </Link>
            <Link
              to="/blog/pillar/ms-fatigue"
              className="group block rounded-2xl border border-primary/15 bg-primary/[0.04] p-6 sm:p-8 hover:bg-primary/[0.06] hover:border-primary/25 transition-colors"
            >
              <div className="eyebrow mb-2">Pillar guide</div>
              <h2 className="font-display text-2xl sm:text-3xl text-foreground leading-tight">
                MS fatigue
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed">
                What it feels like, how long it lingers, and what quietly helps.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-primary group-hover:translate-x-0.5 transition-transform">
                Read the pillar →
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Articles ─────────────────────────────────────── */}
      <section className="bg-background pb-24 pt-12" aria-label="Articles">
        <div className="container mx-auto px-6 sm:px-10 lg:px-16">
          {filtered.length === 0 && (
            <div className="max-w-2xl mx-auto text-center py-16">
              <p className="lead-italic text-muted-foreground">
                Nothing surfaces for that yet. Try another word, or a different category.
              </p>
            </div>
          )}

          {/* Featured */}
          {featured && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10% 0px" }}
              variants={riseFade}
              className="max-w-5xl mx-auto mb-20"
            >
              <Link
                to={`/blog/${featured.slug}`}
                {...prefetchOn(`/blog/${featured.slug}`)}
                className="group block rounded-3xl overflow-hidden border border-foreground/10 bg-background/60 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-foreground/20 hover:shadow-[0_30px_80px_-30px_hsl(var(--foreground)/0.15)]"
              >
                <div className="grid md:grid-cols-2">
                  <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                    <PictureImg
                      src={featured.heroImage}
                      alt={featured.heroAlt}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                      width={600}
                      height={400}
                      loading="eager"
                      fetchPriority="high"
                      sizes="(min-width: 768px) 600px, 100vw"
                    />
                  </div>

                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <span className="eyebrow mb-4">{featured.category}</span>
                    <h2 className="font-display text-foreground text-display-md mb-4 group-hover:text-primary transition-colors">
                      {featured.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed mb-5 line-clamp-3">
                      {featured.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-[12px] text-muted-foreground/80 mb-5">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(featured.publishDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {featured.readTime}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-primary font-medium text-sm group-hover:gap-2.5 transition-all">
                      Read article <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {/* Grid */}
          {rest.length > 0 && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10% 0px" }}
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.06 } },
              }}
              className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {rest.map((article) => (
                <motion.div key={article.slug} variants={riseFade}>
                  <Link
                    to={`/blog/${article.slug}`}
                    {...prefetchOn(`/blog/${article.slug}`)}
                    className="group block rounded-2xl overflow-hidden border border-foreground/10 bg-background/60 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-foreground/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--foreground)/0.15)] h-full"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <PictureImg
                        src={article.heroImage}
                        alt={article.heroAlt}
                        className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                        loading="lazy"
                        width={400}
                        height={225}
                        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      />


                    </div>
                    <div className="p-6">
                      <span className="eyebrow mb-2.5">{article.category}</span>
                      <h2 className="font-display italic text-xl text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2 leading-tight">
                        {article.title}
                      </h2>
                      <p className="text-[13px] text-muted-foreground leading-relaxed mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-[11px] text-muted-foreground/80">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(article.publishDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Newsletter */}
          <div className="max-w-2xl mx-auto mt-24">
            <NewsletterSignup />
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
